/**
 * Transport state. SPEC.md §16.2, §16.3, §16.4.
 *
 * Deliberately separate from the domain store: a flapping radio is not a state
 * mutation, and the two must not invalidate each other's renders. §37
 */

import { create } from 'zustand';
import type { ConnectionState } from '@card-counter/shared';

export interface ConnectionStoreState {
  state: ConnectionState;
  /**
   * The highest sequence the transport has *seen*, including one learned from
   * sync:required while the applied state is still behind. session-store's
   * `sequence` is the one that has actually been applied; a difference between
   * them means a resync is outstanding. §15.2
   */
  lastSequence: number;
  resyncing: boolean;
  /** Epoch ms of when the connection was lost, for "Reconnecting…" copy. §16.5 */
  offlineSince: number | null;
}

export interface ConnectionStoreActions {
  setConnectionState(next: ConnectionState): void;
  setLastSequence(sequence: number): void;
  setResyncing(resyncing: boolean): void;
  reset(): void;
}

export type ConnectionStore = ConnectionStoreState & ConnectionStoreActions;

/**
 * Boots as 'reconnecting', not 'connected': controls stay disabled until
 * authoritative state is actually known. §16.3
 */
const EMPTY_STATE: ConnectionStoreState = {
  state: 'reconnecting',
  lastSequence: 0,
  resyncing: false,
  offlineSince: null,
};

export const useConnectionStore = create<ConnectionStore>()((set) => ({
  ...EMPTY_STATE,

  setConnectionState: (next) =>
    set((current) => {
      if (current.state === next) return current;
      return {
        state: next,
        offlineSince: next === 'connected' ? null : (current.offlineSince ?? Date.now()),
      };
    }),

  setLastSequence: (sequence) =>
    set((current) =>
      sequence > current.lastSequence ? { lastSequence: sequence } : current,
    ),

  setResyncing: (resyncing) =>
    set((current) => (current.resyncing === resyncing ? current : { resyncing })),

  reset: () => set({ ...EMPTY_STATE }),
}));

/**
 * Whether writes may be attempted. V1 has no offline queue — ambiguous ordering
 * and duplicate submissions cost more than the convenience is worth — so the
 * controls are simply disabled until authoritative state is in hand. §16.3, §16.4
 */
export function canInteract(state: ConnectionStoreState): boolean {
  return state.state === 'connected' && !state.resyncing;
}

export const useConnectionState = (): ConnectionState => useConnectionStore((s) => s.state);
export const useOfflineSince = (): number | null => useConnectionStore((s) => s.offlineSince);
